import type { DbExecutor } from "./executor";
import { PlanRepository } from "./repositories/planRepository";
import { RoomRepository } from "./repositories/roomRepository";
import { ObjectDefinitionRepository } from "./repositories/objectDefinitionRepository";
import {
  analyzePlan,
  listProposedEdits,
  revertProposedEdit,
  type PlanModel,
  type ProposedEdit,
} from "../geometry";
import type { PlanSession } from "../design/planSession";

/**
 * PROPOSED CHANGES (spec §61B) — the decisions this plan makes about the
 * existing house, and the ability to cancel any one of them.
 *
 * The list is DERIVED from the two plans by the pure engine; nothing about a
 * decision is stored, exactly as Phase 9 findings are derived. Reverting one
 * runs ordinary geometry operations through the plan session, so it
 * autosaves, undoes and re-scopes like any other edit on the canvas — there
 * is no second edit history and no second persistence path.
 */
export class ProposedEditsService {
  private readonly plans: PlanRepository;
  private readonly rooms: RoomRepository;
  private readonly objectDefs: ObjectDefinitionRepository;

  constructor(private readonly db: DbExecutor) {
    this.plans = new PlanRepository(db);
    this.rooms = new RoomRepository(db);
    this.objectDefs = new ObjectDefinitionRepository(db);
  }

  /** The canonical Existing design of the project this design belongs to. */
  private async existingDesignId(designId: string): Promise<string | null> {
    const rows = await this.db.select<{ id: string }>(
      `SELECT e.id FROM designs d JOIN designs e ON e.project_id = d.project_id AND e.kind = 'existing'
        WHERE d.id = ? AND e.archived_at IS NULL LIMIT 1`,
      [designId],
    );
    return rows[0]?.id ?? null;
  }

  async existingModel(designId: string, floorId: string): Promise<PlanModel | null> {
    const existingId = await this.existingDesignId(designId);
    if (!existingId || existingId === designId) return null;
    return this.plans.loadFloor(existingId, floorId);
  }

  /** Every decision this plan makes on this floor, in reading order. */
  async list(designId: string, floorId: string, proposed?: PlanModel): Promise<ProposedEdit[]> {
    const kind = await this.db.select<{ kind: string }>("SELECT kind FROM designs WHERE id = ?", [designId]);
    if (kind[0]?.kind !== "proposed") return [];
    const plan = proposed ?? (await this.plans.loadFloor(designId, floorId));
    const existing = await this.existingModel(designId, floorId);
    const projectRow = await this.db.select<{ project_id: string }>("SELECT project_id FROM designs WHERE id = ?", [designId]);
    const [rooms, definitions] = await Promise.all([
      this.rooms.listByProject(projectRow[0]?.project_id ?? ""),
      this.objectDefs.listAll(),
    ]);
    return listProposedEdits({
      existing,
      proposed: plan,
      analysis: analyzePlan(plan),
      roomNames: new Map(rooms.map((r) => [r.id, r.name])),
      objectNames: new Map(definitions.map((d) => [d.id, d.displayName])),
    });
  }

  /**
   * Cancel one decision. The session commits it as a normal operation, so
   * the plan autosaves, the change is undoable in this sitting, and scope
   * follows through the coordinator — a restored window is the SAME window
   * it always was, not a new one.
   */
  async revert(session: PlanSession, edit: ProposedEdit): Promise<void> {
    const snapshot = session.snapshot();
    const existing = await this.existingModel(snapshot.model.designId, snapshot.model.floorId);
    session.commit(`Revert: ${edit.summary}`, (model, ctx) => revertProposedEdit(model, ctx, edit, existing));
    await session.flush();
  }
}
